import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import { useSubscription } from '../../contexts/SubscriptionContext';

import logo from '../../assets/logo.png';
import backIcon from '../../assets/icons/back.png';

interface UpgradeScreenProps {
  navigation: NavigationProp<any>;
}

const UpgradeScreen: React.FC<UpgradeScreenProps> = ({ navigation }) => {
  const { subscription, applyBetaCode, refreshSubscription } = useSubscription();
  const [betaCode, setBetaCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');

  const handleApplyBetaCode = async () => {
    if (!betaCode.trim()) {
      Alert.alert('Error', 'Please enter a beta code');
      return;
    }

    setApplyingCode(true);
    try {
      const result = await applyBetaCode(betaCode.trim());
      if (result.success) {
        Alert.alert('Success', result.message, [
          { text: 'OK', onPress: () => navigation.goBack() }
        ]);
      } else {
        Alert.alert('Invalid Code', result.message);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to apply beta code');
    } finally {
      setApplyingCode(false);
    }
  };

  const handleUpgrade = () => {
    navigation.navigate('SubscriptionManagement');
  };

  const getStatusMessage = () => {
    if (!subscription) return 'Loading subscription status...';

    switch (subscription.subscriptionStatus) {
      case 'expired':
        return 'Your free trial has ended';
      case 'grace_period':
        return 'Your subscription is expiring soon';
      case 'cancelled':
        return 'Your subscription has been cancelled';
      default:
        return subscription.message || 'Upgrade to continue';
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Image source={logo} style={styles.logo} resizeMode="contain" />
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.scrollContent}>
        {/* Status Section */}
        <View style={styles.statusSection}>
          <Text style={styles.statusIcon}>⏰</Text>
          <Text style={styles.statusTitle}>{getStatusMessage()}</Text>
          <Text style={styles.statusSubtitle}>
            You can still view your data, but you need to upgrade to add or edit information.
          </Text>
        </View>

        {/* What You Get Section */}
        <View style={styles.benefitsSection}>
          <Text style={styles.sectionTitle}>With MyFinalKey Premium:</Text>
          <View style={styles.benefitItem}>
            <Text style={styles.benefitIcon}>✓</Text>
            <Text style={styles.benefitText}>Unlimited data entry and editing</Text>
          </View>
          <View style={styles.benefitItem}>
            <Text style={styles.benefitIcon}>✓</Text>
            <Text style={styles.benefitText}>Secure cloud backup of all your information</Text>
          </View>
          <View style={styles.benefitItem}>
            <Text style={styles.benefitIcon}>✓</Text>
            <Text style={styles.benefitText}>Share access with successors and keyholders</Text>
          </View>
          <View style={styles.benefitItem}>
            <Text style={styles.benefitIcon}>✓</Text>
            <Text style={styles.benefitText}>AI-powered assistant (Orion)</Text>
          </View>
          <View style={styles.benefitItem}>
            <Text style={styles.benefitIcon}>✓</Text>
            <Text style={styles.benefitText}>Document storage and management</Text>
          </View>
        </View>

        {/* Pricing Section */}
        <View style={styles.pricingSection}>
          <Text style={styles.sectionTitle}>Choose Your Plan</Text>

          <TouchableOpacity
            style={[styles.planCard, selectedPlan === 'annual' && styles.planCardSelected]}
            onPress={() => setSelectedPlan('annual')}
          >
            <View style={styles.planBadge}>
              <Text style={styles.planBadgeText}>Best Value</Text>
            </View>
            <Text style={styles.planTitle}>Annual</Text>
            <Text style={styles.planPrice}>$99/year</Text>
            <Text style={styles.planSavings}>Save 58% vs monthly</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.planCard, selectedPlan === 'monthly' && styles.planCardSelected]}
            onPress={() => setSelectedPlan('monthly')}
          >
            <Text style={styles.planTitle}>Monthly</Text>
            <Text style={styles.planPrice}>$19.99/month</Text>
            <Text style={styles.planDescription}>Cancel anytime</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.upgradeButton} onPress={handleUpgrade}>
            <Text style={styles.upgradeButtonText}>Upgrade Now</Text>
          </TouchableOpacity>
        </View>

        {/* Beta Code Section */}
        <View style={styles.betaSection}>
          <Text style={styles.betaSectionTitle}>Have a Beta or Promo Code?</Text>
          <View style={styles.betaInputRow}>
            <TextInput
              style={styles.betaInput}
              placeholder="Enter code"
              placeholderTextColor="#999"
              value={betaCode}
              onChangeText={setBetaCode}
              autoCapitalize="characters"
              editable={!applyingCode}
            />
            <TouchableOpacity
              style={[styles.applyButton, applyingCode && styles.applyButtonDisabled]}
              onPress={handleApplyBetaCode}
              disabled={applyingCode}
            >
              {applyingCode ? (
                <ActivityIndicator size="small" color="#FFF" />
              ) : (
                <Text style={styles.applyButtonText}>Apply</Text>
              )}
            </TouchableOpacity>
          </View>
          <Text style={styles.betaHint}>
            Beta testers and founding members receive extended free access
          </Text>
        </View>

        {/* Continue Viewing Section */}
        <View style={styles.continueSection}>
          <TouchableOpacity
            style={styles.continueButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.continueButtonText}>Continue Viewing (Read-Only)</Text>
          </TouchableOpacity>
          <Text style={styles.continueHint}>
            You can still view all your saved data
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(10),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  logo: {
    width: s(80),
    height: s(80),
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    padding: ms(20),
    paddingBottom: ms(40),
  },
  statusSection: {
    backgroundColor: '#FFF3CD',
    borderRadius: ms(15),
    padding: ms(20),
    alignItems: 'center',
    marginBottom: ms(25),
    borderWidth: ms(1),
    borderColor: '#FFE69C',
  },
  statusIcon: {
    fontSize: ms(40, 0.3),
    marginBottom: ms(10),
  },
  statusTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#856404',
    textAlign: 'center',
    marginBottom: ms(8),
  },
  statusSubtitle: {
    fontSize: ms(14, 0.3),
    color: '#856404',
    textAlign: 'center',
    lineHeight: ms(20, 0.3),
  },
  benefitsSection: {
    backgroundColor: '#FFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(25),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(15),
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(12),
  },
  benefitIcon: {
    fontSize: ms(18, 0.3),
    color: '#29BFC0',
    marginRight: ms(12),
    fontWeight: 'bold',
  },
  benefitText: {
    fontSize: ms(15, 0.3),
    color: '#333',
    flex: 1,
  },
  pricingSection: {
    marginBottom: ms(25),
  },
  planCard: {
    backgroundColor: '#FFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(15),
    borderWidth: ms(2),
    borderColor: '#E5E7EB',
    position: 'relative',
    overflow: 'hidden',
  },
  planCardSelected: {
    borderColor: '#29BFC0',
    backgroundColor: '#F0FAFA',
  },
  planBadge: {
    position: 'absolute',
    top: ms(10),
    right: ms(10),
    backgroundColor: '#29BFC0',
    paddingHorizontal: ms(10),
    paddingVertical: ms(4),
    borderRadius: ms(10),
  },
  planBadgeText: {
    color: '#FFF',
    fontSize: ms(11, 0.3),
    fontWeight: 'bold',
  },
  planTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(5),
  },
  planPrice: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#29BFC0',
    marginBottom: ms(5),
  },
  planSavings: {
    fontSize: ms(13, 0.3),
    color: '#10B981',
    fontWeight: '600',
  },
  planDescription: {
    fontSize: ms(13, 0.3),
    color: '#6B7280',
  },
  upgradeButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    padding: ms(18),
    alignItems: 'center',
    marginTop: ms(10),
  },
  upgradeButtonText: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#FFF',
  },
  betaSection: {
    backgroundColor: '#FFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(25),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  betaSectionTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(12),
  },
  betaInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(8),
  },
  betaInput: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderRadius: ms(10),
    padding: ms(12),
    fontSize: ms(15, 0.3),
    marginRight: ms(10),
    borderWidth: ms(1),
    borderColor: '#E5E7EB',
  },
  applyButton: {
    backgroundColor: '#1C2C4F',
    borderRadius: ms(10),
    paddingVertical: ms(12),
    paddingHorizontal: ms(20),
  },
  applyButtonDisabled: {
    opacity: 0.6,
  },
  applyButtonText: {
    color: '#FFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  betaHint: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    fontStyle: 'italic',
  },
  continueSection: {
    alignItems: 'center',
  },
  continueButton: {
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
  },
  continueButtonText: {
    fontSize: ms(15, 0.3),
    color: '#6B7280',
    textDecorationLine: 'underline',
  },
  continueHint: {
    fontSize: ms(12, 0.3),
    color: '#9CA3AF',
    marginTop: ms(5),
  },
});

export default UpgradeScreen;
